import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
// The shared base loads before any section, so each section's rules win over it.
import "./tokens.css";
import "./homeEssenziale.css";
import PageMetadata from "../../rendering/PageMetadata";
import { useSeoLinks } from "../../utils/seoLinks";
import { useHomeLang } from "./homeLang";
import DiscHero from "./hero-v2/shapes/DiscHero";
import { HERO_V2_DESCRIPTION, heroV2StructuredData } from "./hero-v2/heroV2Metadata";
import IntroFilm from "./IntroFilm";
import NeedsSection from "./NeedsSection";
import StudioSection from "./StudioSection";
import TrustBanner from "./TrustBanner";
import ProofSection from "./ProofSection";
import TrialSection from "./TrialSection";
import ManifestoSection from "./ManifestoSection";
import FaqSection from "./FaqSection";
import NetworksSection from "./NetworksSection";
import SiteNav from "./SiteNav";
import SiteFooter from "../../components/Footer/Footer";
import { DEFAULT_CLIENTS, parseClients, presenceFor } from "./presence";
import useReducedMotion from "../../lib/useReducedMotion";
import PresenceField from "../../components/Home/PresenceField";
// The headline's weight, fetched as soon as the page's head arrives rather
// than once the stylesheet asks for it.
import poppinsMedium from "@fontsource/poppins/files/poppins-latin-500-normal.woff2";

// avrah.it's gold dust, drifting behind the whole page, kept quiet enough to
// stay behind it. The built page has a script of its own for it, so it draws
// as soon as that small script arrives, before React has attached to the
// page (PresenceField.jsx), and nothing where WebGL2 can't.
const DUST_STRENGTH = 0.35;

// The words beside the studio they describe, the studio on the right.
const INTRO = {
  it: {
    heading: "Il potere del video al servizio della consulenza finanziaria.",
    body: "Realizza videomessaggi, presenta contenuti e moltiplica gli incontri con i tuoi clienti senza moltiplicare il tempo. In call funziona come la tua webcam in Zoom, Teams e Meet. Oppure registri sul tuo computer, in pochi minuti, un video con modelli pronti, il tuo brand e le tue avvertenze, e lo mandi a un cliente o a un gruppo. Video e presentazioni sono elaborati in locale, su Mac o Windows. Niente avatar: ci sei tu, con tutta la forza della tua presenza.",
    film: "Una consulente parla in uno studio con il marchio AVRAH alle spalle",
  },
  en: {
    heading: "The power of video, put to work for financial advice.",
    body: "Record video messages, present your content and multiply your client meetings without multiplying your time. In calls, it works as your webcam in Zoom, Teams and Meet. Or record a video on your computer in minutes, from ready-made templates, with your brand and your disclaimers, and send it to one client or a whole group. Videos and slides are processed locally, on Mac or Windows. No avatars: it’s you, with the full strength of your presence.",
    film: "A financial advisor speaking in a studio, the AVRAH logo behind her",
  },
};

const IntroSection = () => {
  const intro = INTRO[useHomeLang()];
  return (
    <section aria-labelledby="hf-h-intro" className="hf-sec hf-wrap hf-intro">
      <div className="hf-intro-text">
        <h2 id="hf-h-intro" className="t-h2">{intro.heading}</h2>
        <p className="t-body">{intro.body}</p>
      </div>
      <figure className="hf-shot">
        <IntroFilm label={intro.film} />
      </figure>
    </section>
  );
};

export default function HomeEssenziale() {
  const { t } = useTranslation();
  const seo = useSeoLinks("/");
  const [rawClients, setRawClients] = useState(String(DEFAULT_CLIENTS));
  // The clients the hero says don't see the visitor, whom the studio
  // offers to record a video for.
  const { others } = presenceFor(parseClients(rawClients));
  const reducedMotion = useReducedMotion();
  // How far the hero's clients have drawn AVRAH's mark, 0 to 1: the dust's
  // spotlight comes up with it, so the mark lands in full light. A ref the
  // dust reads every frame, so the page never re-renders for it.
  const markDrawn = useRef(0);
  const onMark = useCallback((drawn) => {
    markDrawn.current = drawn;
  }, []);

  return (
    <div className="hf">
      <PresenceField reducedMotion={reducedMotion} strength={DUST_STRENGTH} spotlight={markDrawn} />
      <PageMetadata title={t("home.meta.title")} description={HERO_V2_DESCRIPTION[seo.lang]} seo={seo}>
        <link rel="preload" href={poppinsMedium} as="font" type="font/woff2" crossOrigin="anonymous" />
        <script type="application/ld+json">{heroV2StructuredData(seo)}</script>
      </PageMetadata>
      <SiteNav />
      {/* The header's skip link lands here. */}
      <main id="contenuto" tabIndex={-1}>
        <DiscHero rawClients={rawClients} onClients={setRawClients} onMark={onMark} />
        <IntroSection />
        <NeedsSection />
        <StudioSection others={others} />
        <TrustBanner />
        <ProofSection />
        <TrialSection />
        <FaqSection />
        <NetworksSection />
        <ManifestoSection />
      </main>
      <SiteFooter className="hf-footer" />
    </div>
  );
}
